import { inject, Injectable, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { LottoItemStore } from './lotto-item.store';
import { EntityOptions } from '../../../shared/store/entity-state.feature';
import { REWARD_STATUS } from '../../shared/models/reward-status.enum';
import { Lotto } from './models/lotto.model';

/**
 * Query facade for the LottoItemStore.
 *
 * This class is a thin layer over the store — it exists so that components and services
 * depend on the query, not the store directly. This keeps the store as an implementation
 * detail and makes it easier to refactor or swap out without touching consumers.
 *
 * Naming convention:
 * - `get*` — returns a Signal, use in templates or computed signals that need to react to changes
 * - `select*` — returns an Observable, use in components or services that cannot consume signals directly
 */
@Injectable({
  providedIn: 'root',
})
export class LottoV2Query {
  private lottoStore = inject(LottoItemStore);
  /** Needed so `toObservable` can bridge store signals outside an injection context. */
  private injector = inject(Injector);

  /** Signal — true once the initial list of lottos has been loaded. */
  readonly isLoaded = this.lottoStore.isLoaded;

  /** Signal — map of lotto id to resolved UI status. */
  readonly uiStatusMap = this.lottoStore.uiStatusMap;

  /** Signal — true while an API request is in flight. */
  readonly isLoading = this.lottoStore.isLoading;

  /** Returns a computed signal for a single lotto by id. Reacts to store changes. */
  getLottoById(id: number) {
    return this.lottoStore.getEntity(id);
  }

  /** Returns a computed signal for the resolved UI status of a lotto by id. */
  getStatusById(id: number) {
    return this.lottoStore.getStatusById(id);
  }

  getLottos(options: EntityOptions = {}) {
    return this.lottoStore.getAll(options);
  }

  /** Returns an observable stream of a single lotto by id. */
  selectLottoById(id: number) {
    return this.lottoStore.selectEntity(id);
  }

  /** Returns an observable stream of all lottos sorted by end date. */
  selectLottos(options: EntityOptions = {}) {
    return this.lottoStore.selectAll(options);
  }

  /**
   * Returns a computed signal of the favourited lottos. v2 marks a favourite with
   * `Status === REWARD_STATUS.Favourite` rather than v1's separate `Favourite` flag.
   */
  getFavourites() {
    return this.lottoStore.getAll({ filterBy: (lotto: Lotto) => lotto.Status === REWARD_STATUS.Favourite });
  }

  /** Returns an observable stream of the favourited lottos. @see getFavourites */
  selectFavourites(): Observable<Lotto[]> {
    return toObservable(this.getFavourites(), { injector: this.injector });
  }

  /** Returns an observable stream of the loading state. */
  selectLoading() {
    return this.lottoStore.selectLoading();
  }
}
